import React from 'react'
import { Link, Outlet, getRouteApi, useMatch, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { PageEditHeader } from '@/components/PageEditHeader'
import { Button } from '@/components/Button'
import RecipesTableFilters, { Filters } from './RecipesTableFilters/RecipesTableFilters'
import { RecipesSearch } from '@/routes/recipes'
import { Radio, RadioGroup } from '@/components/RadioGroup'
import { FoodRestriction, RecipeTag, User } from '@/api/types'

const routeApi = getRouteApi('/recipes')

const LIST_VIEW = 'LIST'
const STATS_VIEW = 'STATS'

type Props = {
  tags: RecipeTag[]
  allergens: FoodRestriction[]
  authors: User[]
}

export default function RecipeIndex(props: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const filters: Filters = routeApi.useSearch()

  function setSearchParamsArray(key: keyof RecipesSearch, selection: Set<string>) {
    navigate({
      to: '.',
      search: (search) => ({
        ...search,
        [key]: selection.size > 0 ? Array.from(selection) : undefined,
        page: 0,
      }),
    })
  }

  function setSearchParams(key: string, value: string | number | undefined | boolean) {
    navigate({
      to: '.',
      search: (search) => ({
        ...search,
        [key]: value,
        page: 0,
      }),
    })
  }

  function setSearchParamsObject(obj: RecipesSearch) {
    navigate({
      to: '.',
      search: (search) => ({
        ...search,
        ...obj,
        page: 0,
      }),
    })
  }

  function handleChangeView(value: string | null) {
    const child = value === STATS_VIEW ? 'stats' : ''
    navigate({
      to: `/recipes/${child}`,
      search: (search) => ({
        ...search,
      }),
    })
  }

  const selectedView = useMatch({ from: '/recipes/stats', shouldThrow: false })
    ? STATS_VIEW
    : LIST_VIEW

  return (
    <div>
      <PageEditHeader title={t('recipe.recipes')}>
        <Button asChild variant="primary">
          <Link to="/recipes/new">{t('recipe.newRecipe')}</Link>
        </Button>
      </PageEditHeader>
      <RadioGroup
        aria-label="View type"
        variation="segmented"
        value={selectedView}
        onChange={(value) => handleChangeView(value)}>
        <Radio key={LIST_VIEW} value={LIST_VIEW}>
          {t(`recipe.views.LIST`)}
        </Radio>
        <Radio key={STATS_VIEW} value={STATS_VIEW}>
          {t(`recipe.views.STATS`)}
        </Radio>
      </RadioGroup>
      <div style={{ display: 'flex', gap: '50px', marginTop: '10px' }}>
        <RecipesTableFilters
          tags={props.tags}
          allergens={props.allergens}
          authors={props.authors}
          filters={filters}
          setSearchParams={setSearchParams}
          setSearchParamsArray={setSearchParamsArray}
          setSearchParamsObject={setSearchParamsObject}
        />
        <Outlet />
      </div>
    </div>
  )
}
